import { useEffect, useState } from 'react';
import { Container, Card, Typography, Box, TextField, MenuItem, Button, Alert, Stack } from '@mui/material';
import { supabase } from '../lib/supabaseClient';
import UserIcon from '../components/UserIcon';
import { sanitizeInput, validateUsername, validateUserGroup } from '../lib/validation';
import '../i18n';
import { useTranslation } from 'react-i18next';

const USER_GROUPS = [
  'OS Team',
  'Show Support',
  'Show Management (Bay)',
  'Show Management (Padang)',
  'Artiste Management',
  'Log & Finance',
  'Ops. Safety and Contingencies'
];

export default function Profile() {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState<any>(null);
  const [userGroup, setUserGroup] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('users')
          .select('username, user_group')
          .eq('id', user.id)
          .single();
        if (data) {
          setUserInfo(data);
          if (USER_GROUPS.includes(data.user_group)) {
            setUserGroup(data.user_group);
          } else {
            setUserGroup('');
          }
          setUsername(data.username || '');
        }
        if (error) setError('Failed to load user info');
      }
      setLoading(false);
    };
    fetchUser();
  }, []);

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    const sanitizedUsername = sanitizeInput(username);
    if (!validateUsername(sanitizedUsername)) {
      setError(t('invalid_username'));
      setLoading(false);
      return;
    }
    if (!validateUserGroup(userGroup)) {
      setError('Invalid user group selected.');
      setLoading(false);
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setError('User not found');
      setLoading(false);
      return;
    }
    const { error } = await supabase
      .from('users')
      .update({ user_group: userGroup, username: sanitizedUsername })
      .eq('id', user.id);
    if (error) {
      setError('Failed to update profile: ' + error.message);
    } else {
      setSuccess('Profile updated successfully!');
      // Update shared_location for this user
      await supabase
        .from('shared_location')
        .update({ username, user_group: userGroup })
        .eq('user_id', user.id);
      // Refresh user info to ensure UI reflects the latest data
      const { data } = await supabase
        .from('users')
        .select('username, user_group')
        .eq('id', user.id)
        .single();
      if (data) {
        setUserInfo(data);
        setUserGroup(data.user_group || '');
        setUsername(data.username || '');
      }
    }
    setLoading(false);
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4, px: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column' }}>
      <Card sx={{ p: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1a237e' }}>
          {username}
        </Typography>
        {userInfo && (
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <UserIcon username={username} size={60} />
              <Typography variant="h4" sx={{ ml: 2, fontWeight: 600, color: '#1a237e' }}>
                {username}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" color="text.secondary">
                {t('username')}
              </Typography>
              <TextField
                label={t('username')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle1" color="text.secondary">
                {t('user_group')}
              </Typography>
              <TextField
                select
                label={t('user_group')}
                value={userGroup}
                onChange={(e) => setUserGroup(e.target.value)}
                fullWidth
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                {USER_GROUPS.map((group) => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading}
              sx={{ 
                height: 48,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 6px 8px rgba(0,0,0,0.15)',
                },
              }}
            >
              {t('update_profile')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={async () => {
                await supabase.auth.signOut();
                window.location.href = '/login';
              }}
              sx={{ mt: 3 }}
            >
              {t('logout')}
            </Button>
            {success && <Alert severity="success">{success}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
        {!userInfo && <Typography>Loading...</Typography>}
      </Card>
    </Container>
  );
} 